import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';

/**
 * AgentRunViewer — full step-by-step trace of one agent run.
 *
 * Lives at /runs/:runId. Fetches the persistent event log from
 * /api/jobotics-agent/runs/:runId/events. Renders every SSE event the
 * server emitted (step, partial, tool_progress, result, error, ...)
 * in chronological order with timestamps and payload preview.
 *
 * The use case: a user's chat shows "Agent analyzing results... 511s"
 * and the page eventually refreshes (or they reload). Without this
 * viewer they have no idea what the agent was doing. With it, they
 * pop open the trace and see every tool call, every progress update,
 * and the final response (or the error that killed it).
 */
export default function AgentRunViewer() {
  const { runId } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchOnce = async () => {
      try {
        const r = await fetch(`/api/jobotics-agent/runs/${encodeURIComponent(runId)}/events`, {
          credentials: 'include',
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const json = await r.json();
        if (!cancelled) setData(json);
      } catch (e) {
        if (!cancelled) setErr(e.message);
      }
    };
    fetchOnce();
    // If the run is still running, poll every 3s so the user can
    // watch a live run finish without manually refreshing.
    const interval = setInterval(() => {
      if (cancelled) return;
      if (!autoRefresh) return;
      if (data?.run?.status === 'done' || data?.run?.status === 'error') return;
      fetchOnce();
    }, 3000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [runId, autoRefresh, data?.run?.status]);

  if (err) return <div className="max-w-4xl mx-auto p-8 text-rose-700">{err}</div>;
  if (!data) return <div className="max-w-4xl mx-auto p-8 text-slate-500">Loading run…</div>;

  const { run, events } = data;
  const isDone = run.status === 'done' || run.status === 'error';

  return (
    <div className="max-w-4xl mx-auto px-6 py-8">
      <header className="mb-6">
        <Link to="/" className="text-xs text-slate-500 hover:text-slate-700">← Home</Link>
        <h1 className="text-2xl font-bold text-slate-900 mt-1">
          Activity log
        </h1>
        <div className="mt-2 text-sm text-slate-700">
          {run.agent_id && <div><span className="text-slate-500">Agent:</span> {run.agent_id}</div>}
          <div><span className="text-slate-500">Started:</span> {new Date(run.started_at).toLocaleString()}</div>
          {run.finished_at && (
            <div><span className="text-slate-500">Finished:</span> {new Date(run.finished_at).toLocaleString()} <span className="text-xs text-slate-500">(took {Math.round((new Date(run.finished_at) - new Date(run.started_at)) / 1000)}s)</span></div>
          )}
          <div className="mt-1">
            <StatusPill status={run.status} />
            <span className="ml-3 text-xs text-slate-500">{events.length} step{events.length === 1 ? '' : 's'}</span>
          </div>
        </div>
        {run.query && (
          <div className="mt-3 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800">
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Your question</div>
            {run.query}
          </div>
        )}
        {!isDone && (
          <div className="mt-3 text-xs text-slate-500">
            <label className="inline-flex items-center gap-2">
              <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />
              Refresh automatically every 3 seconds (the agent is still working)
            </label>
          </div>
        )}
      </header>

      <div className="space-y-2">
        {events.map((ev) => <EventRow key={ev.seq} ev={ev} startedAt={run.started_at} />)}
        {events.length === 0 && (
          <div className="text-sm text-slate-500 italic">No activity yet.</div>
        )}
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const tone = ({
    done: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    error: 'bg-rose-50 text-rose-800 border-rose-200',
    running: 'bg-amber-50 text-amber-800 border-amber-200',
    orphaned: 'bg-slate-100 text-slate-700 border-slate-300',
  })[status] || 'bg-slate-50 text-slate-700 border-slate-200';
  const label = ({
    done: 'completed',
    error: 'failed',
    running: 'in progress',
    orphaned: 'stalled',
  })[status] || status;
  return (
    <span className={`inline-block rounded-full border px-2 py-0.5 text-xs font-medium ${tone}`}>
      {label}
    </span>
  );
}

const EVENT_COLOR = {
  run_start:     'border-slate-300 bg-slate-50',
  step:          'border-indigo-200 bg-indigo-50/40',
  partial:       'border-slate-200 bg-white',
  tool_progress: 'border-teal-200 bg-teal-50/40',
  result:        'border-emerald-300 bg-emerald-50',
  error:         'border-rose-300 bg-rose-50',
  done:          'border-slate-300 bg-slate-50',
};

// Plain-English labels for the event-type column.
const EVENT_LABEL = {
  run_start:     'Started',
  step:          'Step',
  partial:       'Thinking',
  tool_progress: 'Progress',
  result:        'Result',
  error:         'Error',
  done:          'Done',
};

function EventRow({ ev, startedAt }) {
  const [open, setOpen] = useState(ev.event_type === 'result' || ev.event_type === 'error');
  const tone = EVENT_COLOR[ev.event_type] || 'border-slate-200 bg-white';
  const dt = new Date(ev.created_at);
  const offsetMs = startedAt ? dt - new Date(startedAt) : 0;
  const offsetStr = `+${(offsetMs / 1000).toFixed(1)}s`;
  const data = ev.data || {};

  // Summary line — varies by event type
  let summary;
  if (ev.event_type === 'step') {
    summary = `${data.id || '(step)'}${data.tool ? ` [${data.tool}]` : ''}${data.label ? ` — ${data.label}` : ''}${data.status ? ` (${data.status}${data.duration ? `, ${data.duration}` : ''})` : ''}`;
  } else if (ev.event_type === 'tool_progress') {
    summary = `${data.tool || 'progress'}: ${data.rowsEmitted ?? '?'} rows${data.pagesProcessed ? ` · p${data.pagesProcessed}` : ''} · ${data.elapsedSec ?? '?'}s`;
  } else if (ev.event_type === 'partial') {
    summary = (data.text || '').slice(0, 140) + ((data.text || '').length > 140 ? '…' : '');
  } else if (ev.event_type === 'result') {
    summary = (data.text || '').slice(0, 140) + ((data.text || '').length > 140 ? '…' : '');
  } else if (ev.event_type === 'error') {
    summary = data.message || '(error)';
  } else if (ev.event_type === 'run_start') {
    summary = `run started · ${data.runId || ''}`;
  } else {
    summary = JSON.stringify(data).slice(0, 140);
  }

  return (
    <div className={`rounded border ${tone} px-3 py-2 text-sm`}>
      <button onClick={() => setOpen(!open)} className="w-full text-left flex items-baseline gap-3">
        <span className="text-xs font-mono text-slate-500 w-12 text-right">{offsetStr}</span>
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-600 w-28">{EVENT_LABEL[ev.event_type] || ev.event_type}</span>
        <span className="flex-1 text-slate-800 break-words">{summary}</span>
        <span className="text-xs text-slate-400">{open ? '▾' : '▸'}</span>
      </button>
      {open && (
        <pre className="mt-2 ml-[10rem] text-[11px] text-slate-700 bg-white/60 border border-slate-200 rounded p-2 overflow-x-auto whitespace-pre-wrap break-words">
{JSON.stringify(data, null, 2)}
        </pre>
      )}
    </div>
  );
}
